import type { User } from '../types';
import { getMetaPlanPayload } from './metaPixel';
import { getStoredTwclid } from './attribution';

const X_EVENT_IDS = {
  SignUp: 'tw-rd5wt-rd5wy',
  AddPaymentInfo: 'tw-rd5wt-rd72w',
  Subscribe: 'tw-rd5wt-rd72x',
  BannerGenerated: 'tw-rd5wt-rd72z',
} as const;

// 1 登録 1 回に制限するための localStorage キー接頭辞。
const X_ONCE_PREFIX = 'tasky_x_once:';

type TwqPayload = Record<string, string | number | boolean | string[] | null | undefined>;

type Twq = {
  (command: 'config', pixelId: string): void;
  (command: 'event', eventId: string, data?: TwqPayload): void;
};

declare global {
  interface Window {
    twq?: Twq;
  }
}

const isBrowser = (): boolean => typeof window !== 'undefined';

const trackXEvent = (eventId: string, payload: TwqPayload): boolean => {
  if (!isBrowser() || !window.twq) return false;

  window.twq('event', eventId, payload);
  return true;
};

// once ガード用の識別子。user.id があれば user:<id>、無ければ anonymous。
const getXIdentityToken = (user?: Pick<User, 'id'> | null): string =>
  user?.id ? `user:${user.id}` : 'anonymous';

const hasFiredXOnce = (key: string): boolean => {
  if (!isBrowser()) return false;
  try {
    return window.localStorage.getItem(`${X_ONCE_PREFIX}${key}`) === '1';
  } catch {
    return false;
  }
};

const markFiredXOnce = (key: string): void => {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(`${X_ONCE_PREFIX}${key}`, '1');
  } catch {
    // localStorage が使えない環境では無視する。
  }
};

// GTM/GA4 用の dataLayer へ push する。twq がブロックされていても GA4 側で
// CV を拾えるようにするため、twq の有無に関係なく呼ぶ。
const pushDataLayer = (data: Record<string, unknown>): void => {
  if (!isBrowser()) return;
  try {
    const w = window as unknown as { dataLayer?: unknown[] };
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push(data);
  } catch {
    // dataLayer が使えない環境では無視する。
  }
};

export const trackXAddPaymentInfo = (conversionId: string | null, plan?: string | null): boolean => {
  if (!conversionId) return false;

  const payload = getMetaPlanPayload(plan);
  return trackXEvent(X_EVENT_IDS.AddPaymentInfo, {
    value: payload.value,
    currency: payload.currency,
    conversion_id: conversionId,
  });
};

export const trackXSubscribe = (conversionId: string | null, plan?: string | null): boolean => {
  if (!conversionId) return false;

  const payload = getMetaPlanPayload(plan);
  return trackXEvent(X_EVENT_IDS.Subscribe, {
    value: payload.value,
    currency: payload.currency,
    conversion_id: conversionId,
  });
};

export const trackXBannerGenerated = (conversionId: string | null): boolean => {
  if (!conversionId) return false;

  return trackXEvent(X_EVENT_IDS.BannerGenerated, {
    conversion_id: conversionId,
  });
};

// 新規登録完了（トライアル申し込み）を X と GA4 の両方へ送る。
// once ガードは 1 登録につき twq / dataLayer の両方を 1 回だけに制限する。
export const trackXSignUp = (user?: Pick<User, 'id'> | null): boolean => {
  const onceKey = `SignUp:${getXIdentityToken(user)}`;
  if (hasFiredXOnce(onceKey)) return false;

  const twclid = getStoredTwclid();

  // twq がブロックされていても GA4 側で拾えるよう、twq の成否に関わらず push する。
  pushDataLayer({ event: 'sign_up_complete', twclid });

  const payload: TwqPayload = {};
  if (twclid) payload.twclid = twclid;
  const fired = trackXEvent(X_EVENT_IDS.SignUp, payload);

  markFiredXOnce(onceKey);
  return fired;
};
